import React, { useMemo } from 'react'
import { isNonEmptyNumber, numberFormatWithPrecision } from '@labrav/common'
import { useStyles } from './MicrosoftInstallments.styles'
import { useStyles as usePricingStyles } from '../MicrosoftViewPricing.styles'
import { Divider } from '@mui/material'
import { productDetailDate } from '../../../common/utils/productDetailDate'
import { useSelector } from '../../../store'

export interface InstallmentScheduleItem {
  paymentDate: string
  amount?: number | null
  currency: string
  flatFee?: number
  otherAmount?: number | null
  chargeDate?: string
  pricePerPaymentInUsd?: number | null
  isImmediateInstallment?: boolean
}

interface MicrosoftInstallmentsProps {
  schedule?: InstallmentScheduleItem[]
  tcv?: number
  currency?: string
}

export const MicrosoftInstallments: React.FC<MicrosoftInstallmentsProps> = ({
  schedule = [],
}) => {
  const { classes } = useStyles()
  const { classes: pricingClasses } = usePricingStyles()

  const getDisplayAmount = (item: InstallmentScheduleItem): number | null => {
    return item.pricePerPaymentInUsd || item.amount || null
  }

  return (
    <div
      className={classes.container}
      data-testid="microsoft-installments-container"
    >
      <div
        className={pricingClasses.subHeading}
        data-testid="installment-schedule-heading"
      >
        Installment Schedule ({schedule?.length})
      </div>
      <Divider />
      <div className={classes.table} data-testid="installment-schedule-table">
        <div className={classes.row} data-testid="installment-table-header">
          <div className={classes.headerCell}>Invoice date</div>
          <div className={classes.headerCell}>Installment amount</div>
        </div>
        {schedule.map((item, index) => (
          <div
            key={index}
            className={classes.row}
            data-testid={`installment-row-${index}`}
          >
            <ShowInvoiceDate item={item} />
            <div
              className={classes.cell}
              data-testid={`installment-amount-${index}`}
            >
              <DisplayAmountValue value={getDisplayAmount(item) || undefined} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const ShowInvoiceDate = ({
  item,
}: {
  item: InstallmentScheduleItem
}) => {
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )

  const { modifiedDate: chargeDate } = item?.chargeDate
    ? productDetailDate(item?.chargeDate, timeZone)
    : { modifiedDate: '' }

  const value = useMemo(() => {
    if (item.isImmediateInstallment) {
      return 'Charge immediately when contract starts'
    }
    if (item.chargeDate) {
      return chargeDate
    }
    return '-'
  }, [item, chargeDate])
  return (
    <div className={classes.cell} data-testid="invoice-date-cell">
      {value}
    </div>
  )
}

export const DisplayAmountValue = ({ value }: { value?: number }) => {
  const { classes } = useStyles()

  return (
    <div data-testid="amount-display">
      {isNonEmptyNumber(value) ? (
        <span className={classes.currency} data-testid="currency-symbol">
          $
        </span>
      ) : (
        ''
      )}
      <span className={classes.numberValue} data-testid="amount-value">
        {isNonEmptyNumber(value) ? numberFormatWithPrecision(value!, 5) : '-'}
      </span>
    </div>
  )
}
